import React, { useEffect, useMemo, useState } from "react";
import { IEntityValueDeviceModel, IEntityValueTankModel } from "../../common/models/IEntityValueModels";
import Highcharts, { Options } from "highcharts";
import highcharts3d from "highcharts/highcharts-3d";
import cylinder from "highcharts/modules/cylinder";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { LiquidProductionTATEnum } from "../../common/enums/LiquidProductionTATEnum";
import { DeviceSubTypeEnum } from "../../common/enums/DeviceSubTypeEnum";
import TankTagDisplayValueV2 from "./TankTagDisplayValueV2";
import { isMobile, isTablet } from 'react-device-detect';
import { DeviceTypesEnum } from "../../common/enums/DeviceTypesEnum";
import { StandaloneTankTATEnum } from "../../common/enums/StandaloneTankTATEnum";
import TankCylinderUtils, { ITankTagDisplayValue } from "../tank-cylinder/TankCylinderUtils";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { getFeetAndInchesText } from "../../common/utilities/FeetAndInches";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useEvaluateFeature } from "../../queries/features/UseEvaluateFeature";
import { FeatureConstants } from "../../services/features-data-service/Models";
import _ from "lodash";

highcharts3d(Highcharts);
cylinder(Highcharts);

export type TankCylinderV2Props = {
    tank: IEntityValueTankModel | IEntityValueDeviceModel,
    legendVertical?: boolean;
    designMode?: boolean;
    dense?: boolean;
};

export default function TankCylinderV2({ tank,
    legendVertical,
    designMode,
    dense

}: TankCylinderV2Props) {

    const routeContext = useRouteContext();
    const theme = useTheme();
    const [chart, setChart] = useState<Highcharts.Chart>();
    const [options, setOptions] = useState<Options | undefined>();
    const [values, setValues] = useState<ITankTagDisplayValue[]>([]);
    const isPhone = isMobile && !isTablet;
    
    const chartID = designMode ? `${tank.id}-chart-dm` : `${tank.id}-chart`;

    const { data: headerAndActionsV2Feature } = useQueryNotifications(useEvaluateFeature)({
        feature: FeatureConstants.HeaderAndActionsV2,
    });

    const headerAndActionsV2 = (headerAndActionsV2Feature ?? false);

    const tankHeight = getFeetAndInchesText(tank.tankStyle.heightInInches);
    const dualPhase = tank.deviceType === DeviceTypesEnum.InternalTank &&
                        tank.tags.some(o => o.tatID === LiquidProductionTATEnum.InterfaceLevel) ? true : false;


    useEffect(() => {
        const tankOptions = TankCylinderUtils.getChartOptions(tank, legendVertical ?? false, theme, true);

        if (routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Tank) {
            tankOptions.chart!.marginTop = 0;
        }

        if (!_.isEqual(options, tankOptions)) {
            setOptions(tankOptions);
        }

    }, [
        options,
        tank,
        setOptions,
        legendVertical,
        theme,
        routeContext.contextObject
    ]);


    useEffect(() => {
        const values: ITankTagDisplayValue[] = [];

        const value = TankCylinderUtils.getTagDisplayText(tank,
            tank.deviceType === DeviceTypesEnum.InternalTank ?
                LiquidProductionTATEnum.TopLevel :
                StandaloneTankTATEnum.LiquidHeight);
        values.push(value);

        const tat = tank.deviceType === DeviceTypesEnum.InternalTank ?
            tank.subTypeID === DeviceSubTypeEnum.Oil ?
                LiquidProductionTATEnum.OilStock :
                LiquidProductionTATEnum.WaterStock :
            StandaloneTankTATEnum.LiquidStock;
        values.push(TankCylinderUtils.getTagDisplayText(tank, tat));

        if (dualPhase) {

            values.push(TankCylinderUtils.getTagDisplayText(tank, LiquidProductionTATEnum.InterfaceLevel));
            values.push(TankCylinderUtils.getTagDisplayText(tank, LiquidProductionTATEnum.WaterStock));
        }

        setValues(values);

    }, [
        tank, 
        setValues,
        dualPhase
    ]);

    

    // const chartCallback = useMemo(() => (c: Highcharts.Chart) => {
    //     if (c) {
    //         c.reflow();
    //         setChart(c);
    //     }
    // }, [setChart]);

    useEffect(() => {

        const chartCallback = (c: Highcharts.Chart) => {
            if (c) {
                c.reflow();
                setChart(c);
            }
        };

        if (options) {
            const currentOptions = { ...options };

            Highcharts.chart(chartID, currentOptions, chartCallback);
        }
    }, [options,
        chartID,
        setChart
        //chartCallback
    ]);

    useEffect(() => {

        let timerID: number | undefined;
        const clearTimer = () => {
            if (timerID) {
                clearTimeout(timerID);
                timerID = undefined;
            }
        };

        const startTimer = () => {

            clearTimer();

            timerID = window.setTimeout(() => {

                if (chart) {
                    //chart.redraw();
                    chart.reflow();
                }

                clearTimer();

            }, 500);
        };

        if (chart) {
            startTimer();
        }

        return () => {
            clearTimer();

        };

    }, [
        chart,
    ]);


    return (
        <Stack
            direction={'column'}
            spacing={1}
            sx={{
                flex: '1 1 auto'
            }}>
            <Typography variant="body2" 
                sx={{ 
                    marginLeft: 'auto', 
                    marginRight: 'auto', 
                    //fontWeight: 600,
                    alignSelf: 'center' 
                }}
            >
                {`Tank Height:  ${tankHeight}`}
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    flex: '1 1 auto',
                    minWidth: headerAndActionsV2 ? 120 : !dense ? 350 : undefined, 
                    position: 'relative',
                    minHeight: isPhone ? 300 : 250,
                    height: '100%'
                }}>

                <Box id={chartID} sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 }}></Box>
            </Box>
            <TankTagDisplayValueV2 
                //values={values} 
                //tank={tank}
                tagValues={values}
                deviceType={tank.deviceType}
                deviceSubType={tank.subTypeID}
                dualPhase={dualPhase}
            />
        </Stack>
    );
}

